/**
 * Fund Manager Dashboard — Bloomberg-Class Overview
 *
 * Information-dense layout with:
 *   - Portfolio overview metrics (total funds, AUM, active investments, investees)
 *   - Compliance status per-fund traffic lights
 *   - Collection progress per fund
 *   - Pipeline distribution
 *   - Activity feed
 *   - Quick actions
 *
 * All data fetched from /api/v1/impact-core/dashboard/* via react-query.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Landmark,
  Briefcase,
  Building2,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  ClipboardCheck,
  RefreshCw,
  AlertTriangle,

  Plus,
  UserPlus,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { TrafficLightBadge, CompletionBar } from '@/components/ui/StatusBadge';
import type {
  PortfolioOverview,
  ComplianceStatus,
  CollectionProgressItem,
  ActivityItem,
  PipelineStage,
} from '@/types';

// ─── Data Fetching ─────────────────────────────────────────────

function usePortfolioOverview() {
  return useQuery<PortfolioOverview>({
    queryKey: ['ic', 'dashboard', 'portfolio-overview'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/dashboard/portfolio-overview');
      return res.data?.data ?? res.data;
    },
    retry: 1,
    staleTime: 60_000,
  });
}

function useComplianceStatus() {
  return useQuery<ComplianceStatus[]>({
    queryKey: ['ic', 'dashboard', 'compliance-status'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/dashboard/compliance-status');
      return res.data?.data ?? res.data ?? [];
    },
    retry: 1,
    staleTime: 60_000,
  });
}

function useCollectionProgress() {
  return useQuery<CollectionProgressItem[]>({
    queryKey: ['ic', 'dashboard', 'collection-progress'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/dashboard/collection-progress');
      return res.data?.data ?? res.data ?? [];
    },
    retry: 1,
    staleTime: 60_000,
  });
}

function useActivityFeed() {
  return useQuery<ActivityItem[]>({
    queryKey: ['ic', 'dashboard', 'activity-feed'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/dashboard/activity-feed');
      return res.data?.data ?? res.data ?? [];
    },
    retry: 1,
    staleTime: 30_000,
  });
}

function usePipeline() {
  return useQuery<PipelineStage[]>({
    queryKey: ['ic', 'dashboard', 'pipeline'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/dashboard/pipeline');
      return res.data?.data ?? res.data ?? [];
    },
    retry: 1,
    staleTime: 60_000,
  });
}

// ─── Fallback Data ─────────────────────────────────────────────

const FALLBACK_PORTFOLIO: PortfolioOverview = {
  total_funds: 0,
  total_aum: 0,
  currency: 'USD',
  active_investments: 0,
  total_investees: 0,
  avg_esg_score: null,
};

const PIPELINE_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#06B6D4', '#F59E0B', '#64748B'];

// ─── Main Component ────────────────────────────────────────────

export function FundManagerDashboard() {
  const navigate = useNavigate();
  const portfolioQuery = usePortfolioOverview();
  const complianceQuery = useComplianceStatus();
  const collectionQuery = useCollectionProgress();
  const activityQuery = useActivityFeed();
  const pipelineQuery = usePipeline();

  const portfolio = portfolioQuery.data ?? FALLBACK_PORTFOLIO;
  const compliance = complianceQuery.data ?? [];
  const collectionItems = collectionQuery.data ?? [];
  const activity = activityQuery.data ?? [];
  const pipeline = pipelineQuery.data ?? [];

  return (
    <div className="space-y-5 ic-page-enter">
      {/* Section: Key Metrics */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard
          label="Total Funds"
          value={(portfolio.total_funds ?? 0).toString()}
          icon={<Landmark className="h-4 w-4" />}
          trend={null}
          loading={portfolioQuery.isLoading}
          accentClass="ic-metric-funds"
        />
        <MetricCard
          label="AUM"
          value={formatCurrency(portfolio.total_aum ?? 0, portfolio.currency ?? 'USD')}
          icon={<TrendingUp className="h-4 w-4" />}
          trend={(portfolio.total_aum ?? 0) > 0 ? { value: 5.6, direction: 'up' } : null}
          loading={portfolioQuery.isLoading}
          accentClass="ic-metric-aum"
        />
        <MetricCard
          label="Active Investments"
          value={(portfolio.active_investments ?? 0).toString()}
          icon={<Briefcase className="h-4 w-4" />}
          trend={(portfolio.active_investments ?? 0) > 0 ? { value: 2, direction: 'up' } : null}
          loading={portfolioQuery.isLoading}
          accentClass="ic-metric-investments"
        />
        <MetricCard
          label="Investees"
          value={(portfolio.total_investees ?? 0).toString()}
          icon={<Building2 className="h-4 w-4" />}
          trend={null}
          loading={portfolioQuery.isLoading}
          accentClass="ic-metric-investees"
        />
      </div>

      {/* Section: Pipeline + Compliance */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Pipeline Chart */}
        <div className="col-span-2 rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
              Investment Pipeline
            </h3>
            <button
              onClick={() => navigate('/pipeline')}
              className="text-[10px] text-ic-accent hover:underline"
            >
              View Board
            </button>
          </div>
          {pipelineQuery.isLoading ? (
            <SkeletonBlock className="h-48" />
          ) : !Array.isArray(pipeline) || pipeline.length === 0 ? (
            <EmptyState message="No pipeline data available" />
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipeline} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: '#64748B' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="stage"
                    tick={{ fontSize: 10, fill: '#94A3B8' }}
                    tickLine={false}
                    axisLine={false}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1E293B',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#E2E8F0',
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                    {pipeline.map((_entry, idx) => (
                      <Cell key={idx} fill={PIPELINE_COLORS[idx % PIPELINE_COLORS.length] ?? '#64748B'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Collection Progress */}
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            Collection Progress
          </h3>
          {collectionQuery.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-8" />)}
            </div>
          ) : !Array.isArray(collectionItems) || collectionItems.length === 0 ? (
            <EmptyState message="No collection data" />
          ) : (
            <div className="space-y-3">
              {collectionItems.map((item) => (
                <CompletionBar
                  key={item.fund_id ?? item.fund_name}
                  percent={item.percent ?? 0}
                  label={item.fund_name ?? 'Unknown Fund'}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Section: Compliance Matrix + Activity */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Compliance Matrix */}
        <div className="col-span-2 rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
              Compliance Status
            </h3>
            <button
              onClick={() => navigate('/compliance')}
              className="text-[10px] text-ic-accent hover:underline"
            >
              View All
            </button>
          </div>
          {complianceQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <SkeletonBlock key={i} className="h-10" />)}
            </div>
          ) : !Array.isArray(compliance) || compliance.length === 0 ? (
            <EmptyState message="No compliance data available" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs ic-table">
                <thead>
                  <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                    <th className="pb-2 pr-4 font-medium">Fund</th>
                    <th className="pb-2 px-2 font-medium text-center">SFDR</th>
                    <th className="pb-2 px-2 font-medium text-center">Taxonomy</th>
                    <th className="pb-2 px-2 font-medium text-center">PAI</th>
                    <th className="pb-2 px-2 font-medium text-right">Data</th>
                    <th className="pb-2 pl-2 font-medium text-right">Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {compliance.map((fund) => (
                    <tr
                      key={fund.fund_id}
                      className="border-b border-ic-border/50 last:border-0 cursor-pointer hover:bg-ic-hover/50"
                      onClick={() => navigate('/compliance')}
                    >
                      <td className="py-2.5 pr-4 text-xs font-medium text-ic-text-primary">
                        {fund.fund_name ?? '--'}
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        {fund.sfdr_status ? <TrafficLightBadge status={fund.sfdr_status} /> : <span className="text-xs text-ic-text-tertiary">--</span>}
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        {fund.taxonomy_status ? <TrafficLightBadge status={fund.taxonomy_status} /> : <span className="text-xs text-ic-text-tertiary">--</span>}
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        {fund.pai_status ? <TrafficLightBadge status={fund.pai_status} /> : <span className="text-xs text-ic-text-tertiary">--</span>}
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <span className="font-mono text-xs text-ic-text-secondary">
                          {fund.data_completeness != null ? `${fund.data_completeness}%` : '--'}
                        </span>
                      </td>
                      <td className="py-2.5 pl-2 text-right text-xs text-ic-text-tertiary">
                        {fund.next_deadline
                          ? (() => { try { return format(parseISO(fund.next_deadline), 'dd MMM yyyy'); } catch { return fund.next_deadline; } })()
                          : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity Feed */}
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
              Activity
            </h3>
            <button
              onClick={() => activityQuery.refetch()}
              className="text-ic-text-tertiary hover:text-ic-text-secondary transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${activityQuery.isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {activityQuery.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => <SkeletonBlock key={i} className="h-12" />)}
            </div>
          ) : !Array.isArray(activity) || activity.length === 0 ? (
            <EmptyState message="No recent activity" />
          ) : (
            <div className="space-y-3">
              {activity.slice(0, 6).map((item) => (
                <div key={item.id ?? Math.random()} className="flex gap-3">
                  <ActivityIcon type={item.type ?? 'system'} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-ic-text-primary">
                      {item.title ?? 'Untitled'}
                    </p>
                    <p className="truncate text-[10px] text-ic-text-tertiary">
                      {item.description ?? ''}
                    </p>
                    <p className="mt-0.5 text-[10px] text-ic-text-tertiary/70">
                      {item.timestamp ? formatRelative(item.timestamp) : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Section: Quick Actions */}
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-2">
          <QuickAction icon={<Plus className="h-3.5 w-3.5" />} label="Create Fund" onClick={() => navigate('/funds')} />
          <QuickAction icon={<Briefcase className="h-3.5 w-3.5" />} label="Add Investment" onClick={() => navigate('/investments')} />
          <QuickAction icon={<UserPlus className="h-3.5 w-3.5" />} label="Invite Investee" onClick={() => navigate('/investees')} />
          <QuickAction icon={<BarChart3 className="h-3.5 w-3.5" />} label="Generate Report" onClick={() => navigate('/reports')} />
          <QuickAction icon={<FileText className="h-3.5 w-3.5" />} label="SFDR Filing" onClick={() => navigate('/compliance')} />
          <QuickAction icon={<ClipboardCheck className="h-3.5 w-3.5" />} label="Send Data Request" onClick={() => navigate('/esg')} />
        </div>
      </div>
    </div>
  );
}

// ─── Sub-Components ────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  trend: { value: number; direction: 'up' | 'down' } | null;
  loading?: boolean;
  accentClass?: string;
}

function MetricCard({ label, value, icon, trend, loading, accentClass }: MetricCardProps) {
  if (loading) {
    return (
      <div className={`rounded-lg border border-ic-border bg-ic-card p-3.5 shadow-xs ${accentClass ?? ''}`}>
        <div className="flex items-center justify-between">
          <span className="text-ic-text-tertiary">{icon}</span>
        </div>
        <div className="mt-2 h-6 w-20 animate-pulse rounded bg-slate-700/50" />
        <div className="mt-1.5 h-3 w-16 animate-pulse rounded bg-slate-700/30" />
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-ic-border bg-ic-card p-3.5 shadow-xs ${accentClass ?? ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ic-text-tertiary">{label}</span>
        {trend && (
          <span
            className={`flex items-center gap-0.5 text-[10px] font-medium ${
              trend.direction === 'up' ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {trend.direction === 'up' ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {trend.value}%
          </span>
        )}
      </div>
      <p className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-ic-text-primary tracking-tight">{value}</p>
      <div className="mt-1 flex items-center gap-1.5 text-ic-text-tertiary">
        {icon}
      </div>
    </div>
  );
}

function ActivityIcon({ type }: { type: ActivityItem['type'] }) {
  const base = 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full';
  switch (type) {
    case 'submission':
      return (
        <div className={`${base} bg-blue-500/15`}>
          <ClipboardCheck className="h-3 w-3 text-blue-400" />
        </div>
      );
    case 'compliance':
      return (
        <div className={`${base} bg-amber-500/15`}>
          <AlertTriangle className="h-3 w-3 text-amber-400" />
        </div>
      );
    case 'investment':
      return (
        <div className={`${base} bg-emerald-500/15`}>
          <Briefcase className="h-3 w-3 text-emerald-400" />
        </div>
      );
    default:
      return (
        <div className={`${base} bg-slate-500/15`}>
          <RefreshCw className="h-3 w-3 text-slate-400" />
        </div>
      );
  }
}

interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

function QuickAction({ icon, label, onClick }: QuickActionProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 rounded-md border border-ic-border bg-ic-card px-3 py-2 text-xs font-medium text-ic-text-secondary hover:border-ic-accent/50 hover:text-ic-accent transition-colors"
    >
      {icon}
      {label}
    </button>
  );
}

function SkeletonBlock({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-slate-700/50 ${className}`} />;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-32 items-center justify-center">
      <p className="text-xs text-ic-text-tertiary">{message}</p>
    </div>
  );
}

// ─── Formatters ────────────────────────────────────────────────

function formatCurrency(amount: number | null | undefined, _currency: string): string {
  const val = amount ?? 0;
  if (val >= 1_000_000_000) return `$${(val / 1_000_000_000).toFixed(1)}B`;
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(0)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  if (val === 0) return '$0';
  return `$${val.toLocaleString()}`;
}

function formatRelative(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;

  return format(parseISO(timestamp), 'dd MMM');
}
